/* eslint-disable @typescript-eslint/no-explicit-any */
import { Button, Flex, Form, message, Modal, Select } from 'antd'
import { useMemo, useState } from 'react'

import {
  useMediaServiceUploadFilesEndpoint,
  useStocksServiceCreateConsumablesImportEndpoint,
  useStocksServiceCreateMedicinesImportEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { DirectInboxIcon, ImportIcon } from 'src/assets'
import { Upload, CustomItemRender, Text, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from './styles'

const acceptedFileTypes = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
]

export const ImportMedicines = () => {
  const { t } = useLocales()
  const { styles } = useStyles()

  const [isOpen, setIsOpen] = useState(false)
  const [form] = Form.useForm()

  const { mutateAsync: upload, isPending: isUploading } =
    useMediaServiceUploadFilesEndpoint({
      onError: (error: ApiError) => formErrorHandler(error.body.errors),
      onSuccess: (data: any) => {
        const id = data?.createdResources[0]?.id

        const values = form.getFieldsValue(true)
        const isConsumable = values.provider === 'consumables'

        isConsumable
          ? importConsumables({
              id,
              requestBody: {
                provider: values.provider
              }
            })
          : importMedicines({
              id,
              requestBody: {
                provider: values.provider
              }
            })
      }
    })

  const { mutate: importMedicines, isPending: isPendingMedicines } =
    useStocksServiceCreateMedicinesImportEndpoint({
      onError: (e: ApiError) => onErrorMutation(e),
      onSuccess: () => onSuccessMutation()
    })

  const { mutate: importConsumables, isPending: isPendingConsumables } =
    useStocksServiceCreateConsumablesImportEndpoint({
      onError: (e: ApiError) => onErrorMutation(e),
      onSuccess: () => onSuccessMutation()
    })

  const onToggle = () => setIsOpen((prev) => !prev)

  const onSubmit = async () => {
    const values = await form.validateFields()

    if (!values.file?.fileList?.length) {
      message.error(t('medicine_register.upload_error'))
      return
    }

    upload({
      folder: values.provider,
      formData: {
        files: [values.file.file.originFileObj]
      }
    })
  }

  const onErrorMutation = (e: ApiError) => {
    if (!e.body) {
      message.error(t('common.server_error'))
      return
    }
    formErrorHandler(e.body?.errors || [])
  }

  const onSuccessMutation = () => {
    message.success(t('common.save_success'))
    window.location.reload()
  }

  const providerOptions = useMemo(
    () => [
      {
        value: 'amdm',
        label: t('medicine_register.products.title')
      },
      {
        value: 'consumables',
        label: t('medicine_register.consumables.title')
      }
    ],
    [t]
  )

  const isLoading = [
    isPendingMedicines,
    isPendingConsumables,
    isUploading
  ].some(Boolean)

  return (
    <>
      <Button type="primary" icon={<ImportIcon />} onClick={onToggle}>
        {t('common.import')}
      </Button>

      <Modal
        open={isOpen}
        onCancel={onToggle}
        afterClose={form.resetFields}
        maskClosable={false}
        centered
        footer={null}
        title={
          <Text className={styles.modalTitle}>
            {t('medicine_register.upload_medicines')}
          </Text>
        }
      >
        <Flex vertical gap={24}>
          <Form form={form} layout="vertical" requiredMark={false}>
            <Flex vertical gap={16}>
              <Form.Item name="file" rules={requiredRules}>
                <Upload
                  acceptedTypes={acceptedFileTypes}
                  maxCount={1}
                  showDescription={false}
                  customRequest={() => null}
                  itemRender={(_, file, __, { remove }) => (
                    <CustomItemRender file={file} onRemove={remove} />
                  )}
                  icon={<DirectInboxIcon />}
                />
              </Form.Item>

              <Form.Item
                name="provider"
                label={t('medicine_register.provider')}
                rules={requiredRules}
              >
                <Select
                  allowClear
                  options={providerOptions}
                  placeholder={t('medicine_register.provider')}
                />
              </Form.Item>
            </Flex>
          </Form>

          <Flex gap={16} justify="center">
            <Button onClick={onToggle}>{t('common.cancel')}</Button>

            <SaveConfirm
              title={`${t('medicine_register.upload_medicines')}?`}
              onConfirm={onSubmit}
              okButtonProps={{ loading: isLoading }}
            >
              <Button type="primary">
                {t('medicine_register.upload_medicines')}
              </Button>
            </SaveConfirm>
          </Flex>
        </Flex>
      </Modal>
    </>
  )
}
